/**
 * Klaviyo form monitoring (#1891).
 *
 * Sends every Klaviyo signup form's activity to GA4, so the forms can be judged against
 * conversion. GA4 loads only where ga4-custom-events.liquid renders it (US); elsewhere
 * these events go nowhere, like the rest of the theme's custom events.
 *   klaviyo_form_open, klaviyo_form_close, klaviyo_form_submit, klaviyo_form_step,
 *   klaviyo_form_redirect   from Klaviyo's klaviyoForms events, every form
 *   klaviyo_blocked         the in-house popup stood in because Klaviyo never answered
 *   klaviyo_teaser_shown    the minimized teaser was added to the page
 *   klaviyo_teaser_seen     ...and was on screen, not covered by anything
 *
 * klaviyo-popup.js keeps its own popup_view / popup_signup events for the dashboards
 * built on them; this file sends alongside them and does not replace them.
 *
 * Nothing is sent until Shopify's Customer Privacy API answers. Events wait in memory
 * (at most WAITING_MAX) until then and are dropped if the shopper declines.
 */
(() => {
  const script = document.currentScript;
  const PAGE_TYPE = script?.dataset.pageType || '';
  // The two forms the popup swap opens from our CTAs (theme settings).
  const OUR_FORMS = (script?.dataset.ourForms || '').split(',').filter(Boolean);

  const TRIGGER_WINDOW = 5000;
  const TEASER_SELECTOR = '[class*="kl-teaser"]';
  const TEASER_POLL = 2000;
  const TEASER_POLL_LIMIT = 60;
  const WAITING_MAX = 50;

  // --- Consent -----------------------------------------------------------------

  function consentStatus() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed !== 'function') return 'pending';
    return privacy.analyticsProcessingAllowed() === false ? 'declined' : 'allowed';
  }

  let waiting = [];

  function deliver(name, params) {
    if (window.GA4CustomEvents?.pushCustomEvent) {
      window.GA4CustomEvents.pushCustomEvent(name, params);
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  }

  function flush() {
    const status = consentStatus();
    if (status === 'pending') return false;
    const events = waiting;
    waiting = [];
    if (status === 'allowed') events.forEach(([name, params]) => deliver(name, params));
    return true;
  }

  function track(name, params) {
    if (waiting.length >= WAITING_MAX) return;
    waiting.push([name, Object.assign({ page_type: PAGE_TYPE }, params)]);
    flush();
  }

  (function waitForConsent() {
    if (flush()) return;
    if (typeof window.Shopify?.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], flush);
    }
    document.addEventListener('visitorConsentCollected', flush);
    let attempts = 0;
    (function poll() {
      if (flush() || ++attempts >= 100) return;
      setTimeout(poll, 100);
    })();
  })();

  // --- Klaviyo form events -------------------------------------------------------

  // klaviyo-popup.js announces each CTA click before it asks Klaviyo to open the form,
  // so an open soon after is attributed to that CTA rather than to Klaviyo's targeting.
  let requested = null;
  document.addEventListener('klaviyo-popup:open-requested', (event) => {
    requested = { source: event.detail?.source || 'cta_click', at: Date.now() };
  });

  const openedAt = {};

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail;
    if (!detail || !detail.type) return;
    const formId = String(detail.formId || detail.formID || detail.id || '');
    const base = {
      form_id: formId,
      form_version_id: String(detail.formVersionId || ''),
      form_kind: OUR_FORMS.includes(formId) ? 'popup_swap' : 'other',
    };

    switch (detail.type) {
      case 'open':
      case 'embedOpen': {
        openedAt[formId] = Date.now();
        let trigger = detail.type === 'embedOpen' ? 'embed' : 'auto';
        if (detail.type === 'open' && requested && Date.now() - requested.at < TRIGGER_WINDOW) {
          trigger = requested.source;
          requested = null;
        }
        track('klaviyo_form_open', Object.assign(base, { display: detail.type === 'open' ? 'popup' : 'embed', trigger }));
        break;
      }
      case 'close':
        track('klaviyo_form_close', Object.assign(base, { seconds_open: secondsSince(formId) }));
        // Klaviyo usually shows the teaser once a popup is closed, which can be long
        // after the first look ran out.
        lookForTeaser();
        break;
      case 'submit':
        track('klaviyo_form_submit', Object.assign(base, { seconds_open: secondsSince(formId) }));
        break;
      case 'stepSubmit':
        // The step name only: metaData also holds what the shopper typed.
        track('klaviyo_form_step', Object.assign(base, { step_name: String(detail.metaData?.$step_name || '') }));
        break;
      case 'redirectedToUrl':
        track('klaviyo_form_redirect', base);
        break;
    }
  });

  function secondsSince(formId) {
    return openedAt[formId] ? Math.round((Date.now() - openedAt[formId]) / 1000) : null;
  }

  // --- Blocked Klaviyo -------------------------------------------------------------

  document.addEventListener('klaviyo-popup:fallback', (event) => {
    // Klaviyo didn't answer this CTA, so a later open isn't its answer.
    requested = null;
    track('klaviyo_blocked', { reason: event.detail?.reason || 'no_open', trigger: event.detail?.source || '' });
  });

  // --- Teaser visibility -----------------------------------------------------------

  // Klaviyo adds the teaser on its own schedule, so it is looked for every few seconds
  // for two minutes after load and again after each popup closes, not with a page-wide
  // MutationObserver.
  let teaserPolls = 0;
  let teaserTimer = null;
  let watched = null;
  let teaserShown = false;
  let teaserSeen = false;

  function lookForTeaser() {
    if (teaserSeen) return;
    teaserPolls = 0;
    clearTimeout(teaserTimer);
    findTeaser();
  }

  function findTeaser() {
    const teaser = document.querySelector(TEASER_SELECTOR);
    if (teaser && teaser !== watched?.teaser) {
      watchTeaser(teaser);
      return;
    }
    if (!watched && ++teaserPolls < TEASER_POLL_LIMIT) teaserTimer = setTimeout(findTeaser, TEASER_POLL);
  }

  function watchTeaser(teaser) {
    watched?.stop();
    if (!teaserShown) {
      teaserShown = true;
      track('klaviyo_teaser_shown', {});
    }

    // Only while it is on screen is it checked for being covered, such as by the mobile
    // sticky add-to-cart bar: the point at its centre must belong to the teaser.
    let onScreen = false;
    let frame = 0;

    function check() {
      frame = 0;
      if (!teaser.isConnected) {
        // Klaviyo swapped the node; follow the new one.
        stop();
        lookForTeaser();
        return;
      }
      if (!onScreen) return;
      const box = teaser.getBoundingClientRect();
      const top = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2);
      if (!top || !teaser.contains(top)) return;
      teaserSeen = true;
      stop();
      track('klaviyo_teaser_seen', {});
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(check);
    }

    const observer = new IntersectionObserver((entries) => {
      onScreen = entries.some((entry) => entry.isIntersecting);
      schedule();
    });

    function onScroll() {
      if (onScreen) schedule();
    }

    function stop() {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
      watched = null;
    }

    observer.observe(teaser);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    watched = { teaser, stop };
  }

  lookForTeaser();
})();
